import type { z } from "zod";

import { CatalogKitConfigSchema, DEFAULT_CONFIG } from "@/types";
import type { CatalogKitConfig } from "@/types";

/**
 * A typed, schema-validated wrapper around `localStorage`. Values are JSON
 * serialized; reads validate against a Zod schema and fall back to a default on
 * any error (missing key, malformed JSON, schema drift, or storage unavailable).
 */
export interface TypedStore<T> {
  load(): T;
  save(value: T): void;
  clear(): void;
}

export function createStore<T>(
  key: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  fallback: T,
): TypedStore<T> {
  return {
    load(): T {
      try {
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        const parsed = schema.safeParse(JSON.parse(raw));
        return parsed.success ? parsed.data : fallback;
      } catch {
        // Storage unavailable or malformed — use the default.
        return fallback;
      }
    },
    save(value: T): void {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Storage may be unavailable (e.g. private mode); ignore.
      }
    },
    clear(): void {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore.
      }
    },
  };
}

/** Persisted CatalogKit configuration. */
export const configStore: TypedStore<CatalogKitConfig> = createStore(
  "catalogkit.config",
  CatalogKitConfigSchema,
  DEFAULT_CONFIG,
);
